import { UI, HStack, VStack } from '@genial/components';
import { BoxBorder, BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import React from 'react';

import {
  TableContent,
  TableContentItemText,
} from '../../../../../../../../../components/TableContent';
import { useGlobalAssetContext } from '../../../../../contexts';
import { useEquityAssetController } from '../../../../../hooks/products/Equity';
import { AssetTitleWithImage } from '../../../components/AssetTitleWithImage';
import { AssetValueWithPercentage } from '../../../components/AssetValueWithPercentage';
import { LateralBar } from '../../../components/LateralBar';
import { ListLoadingItemMobile } from '../ListLoadingItem/mobile';

import { EQUITY_ORDER_TYPE_LABEL, EQUITY_ORDER_TYPE_OPTIONS, EquityListItemTypes } from '.';

export const EquityListItemMobile: React.FC<EquityListItemTypes.Props> = ({ offer }) => {
  const id = offer.quote.productId;
  const {
    amountItem,
    priceItem,
    orderType,
    handleChangeOrderType,
    totalPrice,
    handleChangeAmountItem,
    handleChangePriceItem,
    totalPriceIsInvalid,
  } = useEquityAssetController(offer);
  const { infoGetter, selectedTab, isBatchTicket } = useGlobalAssetContext();

  const completeOffer = {
    ...offer,
    orderQuantity: amountItem,
    orderValue: priceItem,
    orderType,
  };

  const { Button, isSelected, forceLoading } = infoGetter(
    'equity',
    completeOffer,
    'productIdNormalized'
  );

  const infos = [
    {
      label: 'Melhor Compra',
      value: <UI.Text color="neutral.dark">{offer.bidPriceNormalized}</UI.Text>,
    },
    {
      label: 'Melhor Venda',
      value: <UI.Text color="neutral.dark">{offer.askPriceNormalized}</UI.Text>,
    },
    {
      label: 'Fechamento',
      value: <UI.Text color="neutral.dark">{offer.closePriceNormalized}</UI.Text>,
    },
  ];

  if (forceLoading) {
    return <ListLoadingItemMobile />;
  }

  return (
    <TableContent
      direction="row"
      space={0}
      p={0}
      px={Spacing['inline.xxxs']}
      isHighlighted={isSelected}>
      <LateralBar
        backgroundColor={selectedTab.color}
        borderTopLeftRadius={BoxRadius.sm}
        borderBottomLeftRadius={BoxRadius.sm}
      />
      <VStack padding={Spacing['inset.sm']} space={Spacing['stack.xxxs']} w="full">
        <HStack justifyContent="space-between" alignItems="center">
          <AssetTitleWithImage
            image={offer.imageNormalized || ''}
            title={offer.productNameNormalized || ''}
            subtitle={offer.symbolNormalized || ''}
          />
          <AssetValueWithPercentage
            value={offer.lastPriceNormalized}
            percentage={offer.priceChangeTaxValueNormalized}
            iconColor={offer.priceChangeTaxColorNormalized}
            updateDate={offer.tradeDateNormalized}
          />
        </HStack>
        <HStack justifyContent="space-between">
          {infos.map(({ label, value }) => (
            <VStack space={Spacing['stack.quarck']} key={label}>
              <UI.Text color="neutral.black" variant="semibold">
                {label}
              </UI.Text>
              {value}
            </VStack>
          ))}
        </HStack>
        <VStack space={Spacing['inset.quarck']}>
          <UI.Text color="neutral.black" variant="semibold">
            Tipo da ordem
          </UI.Text>
          {isBatchTicket ? (
            <VStack
              background={Color.background}
              p={Spacing['inset.nano']}
              borderRadius={BoxRadius.sm}>
              <UI.Text color="neutral.black" py={Spacing['stack.quarck']}>
                LIMITADA
              </UI.Text>
            </VStack>
          ) : (
            <VStack borderRadius={BoxBorder.thick} bgColor="backgroundshade">
              <UI.Select
                testID={`equity-order-type-select${id}`}
                options={EQUITY_ORDER_TYPE_OPTIONS}
                selectedValue={orderType}
                disabled={isSelected}
                onValueChange={(value) => {
                  handleChangeOrderType(value as EquityListItemTypes.OrderType);
                }}
                placeholder={EQUITY_ORDER_TYPE_LABEL.Limit}
              />
            </VStack>
          )}
        </VStack>
        <VStack space={Spacing['stack.quarck']} w="full">
          <UI.Text color="neutral.black" variant="semibold">
            Quantidade
          </UI.Text>
          <UI.InputStepper
            stepperValue={offer.quote.lotSize || 1}
            max={1000000}
            min={offer.quote.lotSize || 1}
            name={`equity-amount-order-input${id}`}
            testId={`equity-amount-order-input${id}`}
            keyboardType="numeric"
            fontSize={Typography['fontsize.md']}
            defaultValue={amountItem}
            onChange={handleChangeAmountItem}
            showErrorMessage={false}
            disabled={isSelected}
            {...((totalPrice === 0 || totalPrice > 1000000) && {
              customError: 'Quantidade inválido',
            })}
          />
        </VStack>
        <VStack space={Spacing['stack.quarck']} w="full">
          <UI.Text color="neutral.black" variant="semibold">
            Preço por ativo
          </UI.Text>
          <UI.InputStepper
            stepperValue={0.01}
            max={1000000}
            min={0.01}
            fontSize={Typography['fontsize.md']}
            keyboardType="numeric"
            ignoreStepperDivision
            maskOption="money"
            name={`equity-amount-price${id}`}
            testId={`equity-amount-price${id}`}
            defaultValue={priceItem || 0.01}
            onChange={handleChangePriceItem}
            showErrorMessage={false}
            disabled={isSelected || (!isBatchTicket && orderType === 'Market')}
            {...((totalPrice === 0 || totalPrice > 1000000) && {
              customError: 'Valor inválido',
            })}
          />
        </VStack>
        <HStack justifyContent="space-between">
          <VStack space={Spacing['stack.quarck']} alignSelf="end">
            <UI.Text color="neutral.black">Total estimado</UI.Text>
            <UI.Text variant="semibold" fontSize={Typography['fontsize.lg']}>
              {Utils.Mask.Money(totalPrice)}
            </UI.Text>
            {totalPriceIsInvalid && (
              <TableContentItemText
                fontSize={Typography['fontsize.xs']}
                color="feedback.error.default">
                Valor máximo atingido
              </TableContentItemText>
            )}
          </VStack>
          <Button
            id={selectedTab.typeGetterId}
            offer={completeOffer}
            isDisabled={totalPriceIsInvalid}
            productContextConstant={selectedTab}
          />
        </HStack>
      </VStack>
    </TableContent>
  );
};
